/**
 * MediaPanelContent - New rendering component using slot-based architecture
 *
 * This component replaces PanelBody and uses the new layout system:
 * - Reads SlotRegistry to determine control placement
 * - Renders controls based on location (content-header, content-footer, etc.)
 * - Uses GridData for tile rendering
 *
 * Benefits:
 * - Single source of truth (slots from layout strategies)
 * - No hardcoded control logic
 * - Extensible (easy to add new controls)
 * - Testable (pure rendering from props)
 */

import {type MediaCardType} from '@wandb/weave/common/types/media';
import {Tailwind, TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useEffect, useMemo} from 'react';

import {mediaComparePanelViewed} from '../../services/analytics/productEvents';
import {type CustomRunNames} from '../../state/views/customRunNames/types';
import type * as PanelTypes from '../../state/views/panel/types';
import {type PartRefFromObjSchema} from '../../state/views/types';
import {type RunWithHistoryAndMediaWandb} from '../MediaCard';
import {useDetectPanelVariant} from '../Panel/hooks/hooks';
import {type KeyTypes} from './common';
import {
  type MediaBrowserPanelProps,
  type MediaConfigWithDefaults,
  type SizingSettings,
} from './Component';
import {LayoutConfigProvider} from './layout/contexts/LayoutConfigProvider';
import {
  type ControlData,
  type GridData,
  type RowData,
  type SlotRegistry,
} from './layout/types';
import {MediaPanelContentRenderer} from './MediaPanelContentRenderer';
import {PanelHeader} from './PanelHeader';
import {PanelSettings} from './PanelSettings';
import {type TilePagination} from './tiles';
import {type StepMeta} from './types';
import {getTitleFromConfig} from './utils/getTitleFromConfig';
import {useCompareViewportLifecycle} from './zoom/useCompareViewportLifecycle';

export type MediaPanelContentProps = {
  gridData: GridData;
  slots: SlotRegistry;
  config: MediaConfigWithDefaults;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  stepMeta: StepMeta;
  isMaxStepLoading: boolean;
  mediaBrowserSize: {
    width: number;
    height: number;
  };
  cardWidth: number;
  cardHeight: number;
  mediaType: MediaCardType;
  mediaSliderKey: string;
  panelRef: PartRefFromObjSchema<PanelTypes.PanelObjSchema>;
  disableRunLinks?: boolean;
  customRunNames?: CustomRunNames;
  shrunk?: boolean;
  mediaBrowserRefCallback: (el: HTMLDivElement) => void;
  panelViewerRef: React.MutableRefObject<HTMLDivElement | null>;
  tilePagination: TilePagination | null;
  allMasks?: any;
  controlData?: ControlData[];
  rowData?: RowData[];
  // Panel editor preview mode, used by settings and viewport renderers.
  configMode?: boolean;
  sizingSettings: SizingSettings;
  allMediaKeys: string[];
  keyTypes: KeyTypes;
  numericKeys?: string[];
  availableRuns: RunWithHistoryAndMediaWandb[];
};

export const MediaPanelContent: React.FC<MediaPanelContentProps> = ({
  gridData,
  slots,
  config,
  updateConfig,
  stepMeta,
  isMaxStepLoading,
  mediaBrowserSize,
  cardWidth,
  cardHeight,
  mediaType,
  mediaSliderKey,
  panelRef,
  disableRunLinks,
  customRunNames,
  shrunk,
  mediaBrowserRefCallback,
  panelViewerRef,
  tilePagination,
  allMasks,
  controlData,
  rowData,
  configMode = false,
  sizingSettings,
  allMediaKeys,
  keyTypes,
  numericKeys,
  availableRuns,
}) => {
  const {isFullScreenPanelPage} = useDetectPanelVariant();
  // The editor preview shares the live panel's ID, so it must not publish
  // viewport state over the live panel's entry.
  useCompareViewportLifecycle({config, mediaType, enabled: !configMode});

  const chartTitle = getTitleFromConfig(config);
  const isFullScreen = isFullScreenPanelPage === true;
  const isCompare = config.mode === 'compare';
  useEffect(() => {
    if (isCompare) {
      mediaComparePanelViewed({
        view: isFullScreen ? 'fullscreen' : 'workspace',
      });
    }
  }, [isCompare, isFullScreen]);
  const panelHeaderConfig = useMemo(
    () => ({...config, chartTitle}),
    [config, chartTitle]
  );

  const content = (
    <LayoutConfigProvider
      mediaType={mediaType}
      config={config}
      updateConfig={updateConfig}
      availableRuns={availableRuns}
      availableMediaKeys={allMediaKeys}
      stepMeta={stepMeta}
      gridData={gridData}
      controlData={controlData}
      rowData={rowData}
      columnWidth={cardWidth}
      configMode={configMode}>
      <div className="flex h-full flex-col">
        {/* Panel Header - Chart Title */}
        {chartTitle && <PanelHeader config={panelHeaderConfig} />}

        {/* Main Content Area with sizing */}
        <div className="w-full flex-1 overflow-auto min-h-0 flex flex-col">
          <MediaPanelContentRenderer
            mode={config.mode || 'gallery'}
            gridData={gridData}
            slots={slots}
            config={config}
            updateConfig={updateConfig}
            stepMeta={stepMeta}
            isMaxStepLoading={isMaxStepLoading}
            mediaBrowserSize={mediaBrowserSize}
            cardWidth={cardWidth}
            cardHeight={cardHeight}
            mediaType={mediaType}
            mediaSliderKey={mediaSliderKey}
            panelRef={panelRef}
            disableRunLinks={disableRunLinks}
            customRunNames={customRunNames}
            shrunk={shrunk}
            allMasks={allMasks}
            tilePagination={tilePagination}
            mediaBrowserRefCallback={mediaBrowserRefCallback}
            panelViewerRef={panelViewerRef}
            isFullScreenPanelPage={isFullScreen}
          />
        </div>
      </div>
    </LayoutConfigProvider>
  );

  // Render with config mode sidebar if enabled
  if (configMode) {
    return (
      <Tailwind>
        <div className="panel-media__config-container">
          <div className="panel-media__config-content">{content}</div>
          <div className="panel-media__config-settings">
            <PanelSettings
              mediaType={mediaType}
              sizingSettings={sizingSettings}
              allMediaKeys={allMediaKeys}
              numericKeys={numericKeys}
              config={config}
              updateConfig={updateConfig}
              keyTypes={keyTypes}
              historySampleRate={gridData.metadata.historySampleRate}
            />
          </div>
        </div>
      </Tailwind>
    );
  }

  return (
    <TailwindContents>
      <div data-test="media-panel" className="panel-media__container">
        {content}
      </div>
    </TailwindContents>
  );
};

export default MediaPanelContent;
